import { CommonModule, NgIf } from '@angular/common';
import {
  AfterViewInit,
  Component,
  effect,
  EnvironmentInjector,
  input,
  model,
  output,
  signal,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';

import { DTO, Types } from 'shared-lib';
import {
  EComponentSize,
  EIcon,
  ELabelType,
  FilterSectionItem,
  FormItem,
  IComponentSection,
} from '../../types';
import { StepProgressComponent } from '../step-progress/step-progress.component';
import {
  components,
  directive,
  LoadingService,
  ToastService,
} from '../../../public-api';
import { LabelComponent } from '../label/label.component';
import { ButtonComponent } from '../button/button.component';
import { SectionComponent } from '../section/section.component';

@Component({
  selector: 'ui-dynamic-form',
  standalone: true,
  imports: [
    NgIf,
    StepProgressComponent,
    LabelComponent,
    ButtonComponent,
    CommonModule
],
  templateUrl: './dynamic-form.component.html',
  styleUrl: './dynamic-form.component.scss',
})
@directive.Flowbite()
export class DynamicFormComponent implements AfterViewInit {
  form = signal<IComponentSection[]>([]);
  formName = input<string>();
  showButtons = input<boolean>(true);
  finalSubmit = input<string>('Cadastrar');
  inList = model<boolean>(false);
  useSteps = input<boolean>(true);
  withBorder = input<boolean>(false);
  fields = input.required<IComponentSection[]>();
  currentStage = signal(0);
  loading = signal(true);
  formData: { [key: string]: any } = {};
  valueChange = output<{ [key: string]: any }>();

  @ViewChild('container', { read: ViewContainerRef })
  container?: ViewContainerRef;

  formSignals: FormItem[][] = [];
  formSignalsSection: FilterSectionItem[] = [];
  submitName =
    this.currentStage() < this.form().length - 1
      ? 'Próximo'
      : this.finalSubmit();
  resultForm?: IComponentSection[];
  ELabeltype = ELabelType;
  EComponentSize = EComponentSize;
  EIcon = EIcon;
  EAlert = Types.EAlert;
  EList = Types.EList;

  constructor(
    private envInjector: EnvironmentInjector,
    private toastService: ToastService,
    private loadingService: LoadingService
  ) {
    this.loading = this.loadingService.loading;
    this.loadingService.start();
    effect(
      () => {
        this.changeForm();
        if (this.currentStage() >= 0 && this.form().length > 0) {
          this.renderComponents();
        }

        if (this.useSteps()){
          this.submitName =
          this.currentStage() < this.form().length - 1
            ? 'Próximo'
            : this.finalSubmit();
        }else{
          this.submitName = this.finalSubmit();
        }

      },
      {
        allowSignalWrites: true,
      }
    );
  }

  initFormSignals(length: number) {
    this.formSignals = [];
    for (let i = 0; i < length; i++) {
      this.formSignals.push([]);
    }
  }

  ngAfterViewInit(): void {
    this.changeForm();
  }

  changeForm(){
    this.formSignals = [];
    this.formSignalsSection = [];
    this.resultForm = structuredClone(this.fields());
    this.initFormSignals(this.resultForm.length);
    this.form.set(this.fields());
    this.container?.clear()
    this.unRenderComponents();
    this.currentStage.set(0);
  }

  onBack(event: Event) {
    event.preventDefault();
    this.loading.set(true);
    if (this.currentStage() > 0) {
      this.unRenderComponents();
      this.currentStage.update((value) => value - 1);
    }
    this.loading.set(false);
  }

  onKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      this.onSubmit(event);
    }
  }

  onSubmit(event: Event) {
    event.preventDefault();
    this.loading.set(true);
    if (this.useSteps()) {
      if (this.currentStage() < this.form().length - 1) {
        this.unRenderComponents();
        this.currentStage.update((value) => value + 1);
        this.loadingService.stop();
        this.loading.set(false);
        return;
      }
    }
    this.returnData();
    this.loadingService.stop();
    this.loading.set(false);
  }

  returnData() {
    this.collectDataFromCells();
    const formValidation = this.validateForm();
    if (!this.resultForm || !formValidation) {
      return;
    }

    for (let i = 0; i < this.resultForm.length; i++) {
      const result = this.resultForm[i];
      let dataInput: { [key: string]: any } = {};

      for (let j = 0; j < result.content.length; j++) {
        const input = result.content[j];
        dataInput[input.selector] = input.inputs?.value ?? null;
      }

      this.formData[result.selector ?? 'data'] = dataInput;
    }

    this.valueChange.emit(this.formData);
  }

  onTab(id: number) {
    this.loading.set(true);
    this.unRenderComponents();
    this.currentStage.set(id);
  }

  collectDataFromCells() {
    if (!this.form() || !this.resultForm) {
      this.loadingService.stop();
      return;
    }
    let arraySize = this.useSteps()
      ? this.formSignals.length
      : this.formSignalsSection.length;
    for (let j = 0; j < arraySize; j++) {
      const formSignal = this.useSteps()
        ? this.formSignals[j]
        : (this.formSignalsSection[j]
            .components()
            .filter(
              (component) => component instanceof FormItem
            ) as FormItem[]);
      for (let i = 0; i < formSignal.length; i++) {
        const form = formSignal[i];
        const templateItemDTO = new DTO.TemplateItemDTO();
        templateItemDTO.inputs = new DTO.TemplateItemInputsDTO();
        templateItemDTO.component = this.form()[j].content[i].component;
        templateItemDTO.selector = this.form()[j].content[i].selector;
        for (const input of Object.keys(form.inputs)) {
          (templateItemDTO.inputs as any)[input] = form.inputs[input]();
        }
        this.resultForm[j].content[i] = templateItemDTO;
      }
    }
  }

  validateForm() {
    if (!this.resultForm) {
      this.loadingService.stop();
      return false;
    }
    for (let form of this.resultForm) {
      for (let field of form.content) {
        if (
          field.inputs.required &&
          (field.inputs.value === '' ||
            field.inputs.value === undefined ||
            field.inputs.value === null)
        ) {
          this.toastService.add(
            `O campo "${field.inputs.placeHolder}" é obrigatório!`,
            30000,
            'danger'
          );
          this.loadingService.stop();
          return false;
        }
      }
    }
    this.loadingService.stop();
    return true;
  }

  renderComponents() {
    const currentStage = structuredClone(this.currentStage());
    if (!this.form()) {
      this.loading.set(false);
      return;
    }
    if (currentStage < 0 || currentStage > this.form().length) {
      this.loading.set(false);
      return;
    }

    this.loading.set(true);
    if (this.useSteps()) {
      if (this.formSignals[currentStage]?.length ?? 0 > 0) {
        for (let i = 0; i < this.formSignals[currentStage].length; i++) {
          const component = this.formSignals[currentStage][i].component;
          this.container?.insert(component.hostView);
        }
      } else {
        for (let i = 0; i < this.form()[currentStage].content.length; i++) {
          const form = this.form()[currentStage].content[i];
          const component = this.container?.createComponent(
            (components as any)[form.component],
            {
              environmentInjector: this.envInjector,
            }
          );
          if (!component) {
            return;
          }
          const templateItemDTO = new FormItem();
          templateItemDTO.component = component;
          templateItemDTO.selector = form.selector;
          (templateItemDTO.inputs as any) = {};
          component.setInput('editable', form.inputs.editable ?? true);
          component.setInput('value', '');
          (templateItemDTO.inputs as any)['value'] = (
            component.instance as any
          )['value'];
          for (const input of Object.keys(form.inputs)) {
            component.setInput(input, (form.inputs as any)[input]);
            (templateItemDTO.inputs as any)[input] = (
              component.instance as any
            )[input];
          }
          this.formSignals[currentStage].push(templateItemDTO);
        }
      }
    } else {
      if (this.formSignalsSection?.length ?? 0 > 0) {
        for (let i = 0; i < this.formSignalsSection.length; i++) {
          const component = this.formSignalsSection[i].component;
          this.container?.insert(component.hostView);
        }
      } else {
        for (let j = 0; j < this.fields().length; j++) {
          const component = this.container?.createComponent(
            SectionComponent,
            {
              environmentInjector: this.envInjector,
            }
          );
          if (!component) {
            this.loadingService.stop();
            return;
          }
          const itemDTO = new FilterSectionItem();
          itemDTO.component = component;
          component.setInput('name', this.fields()[j].name);
          component.setInput('filters', this.fields()[j].content);
          component.setInput('inList', this.inList());
          component.setInput('withBorder', this.withBorder());
          itemDTO.components = (component.instance as any)[
            'formSignalsSection'
          ];
          this.formSignalsSection.push(itemDTO);
        }
      }
    }
    this.loading.set(false);
  }

  unRenderComponents() {
    const currentStage = structuredClone(this.currentStage());
    if (!this.formSignals[currentStage]) {
      return;
    }
    for (const formSignal of this.formSignals[currentStage]) {
      const index = this.container?.indexOf(formSignal.component.hostView);
      this.container?.detach(index);
    }
  }
}
